import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, ChevronLeft, ChevronRight, Star, Eye } from 'lucide-react';
import { trackEvent } from '../utils/analyticsTracker';
import { usePageView } from '../utils/usePageView';

/* ─────────────────────────────────────────────
   Phase preview data — Q1 W1 L1 from each phase
   (same data as HomePage — shared content)
   ───────────────────────────────────────────── */
const PHASE_PREVIEWS = {
  1: {
    title: 'Animal Sounds — The Noises Animals Make!',
    theme: 'Sound Recognition & Imitation',
    icon: '/assets/P1F.webp',
    color: 'from-pink-400 to-rose-500',
    accent: { border: 'border-pink-300', bg: 'bg-pink-50', text: 'text-pink-700', dot: 'bg-pink-400' },
    ages: '1-2',
    phaseName: 'Foundations',
    stepName: 'Warm-Up: Animal Sounds',
    hook: 'Introduce your child to the idea that sounds carry meaning.',
    script: [
      '"Do you hear that? MOOO! That\'s a cow!"',
      '"Can you say moo with me? Mooooo!"',
    ],
    doThis: [
      'Make 3 animal sounds together',
      'Cheer when your child copies a sound',
    ],
    watchFor: 'Does your child try to imitate the sound?',
    outcomeTag: 'Builds sound recognition — the first step to reading.',
    kicker: 'You just taught sound-meaning connection. Every reading skill builds on this.',
  },
  2: {
    title: 'Point and Name — Everyday Objects',
    theme: 'Object Naming & Vocabulary',
    icon: '/assets/P2F.webp',
    color: 'from-green-400 to-teal-500',
    accent: { border: 'border-teal-300', bg: 'bg-teal-50', text: 'text-teal-700', dot: 'bg-teal-400' },
    ages: '2-3',
    phaseName: 'Explorers',
    stepName: 'Warm-Up: Point and Name',
    hook: 'Your child connects words to things they can see and touch.',
    script: [
      '"Look! A cup. CUP. Can you say cup?"',
      '"Point to the cup! Good job!"',
    ],
    doThis: [
      'Pick 3 objects nearby and name them slowly',
      'Ask your child to point to each one',
    ],
    watchFor: 'Does your child point or try to say the word?',
    outcomeTag: 'Builds vocabulary — the words your child will one day read.',
    kicker: 'You just built vocabulary foundations — the building blocks your child needs to read.',
  },
  3: {
    title: 'A is for Apple — Meet Your First Letter!',
    theme: 'Letter Introduction & Sounds',
    icon: '/assets/P3F.webp',
    color: 'from-blue-400 to-indigo-500',
    accent: { border: 'border-blue-300', bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-400' },
    ages: '3-4',
    phaseName: 'Thinkers',
    stepName: 'Warm-Up: Meet the Letter A',
    hook: 'Introduce the letter A and its sound through air drawing and verbal play.',
    script: [
      '"Today we\'re meeting a special letter! This is A!"',
      '"A makes the sound /a/ — like apple! Aaaa-pple!"',
    ],
    doThis: [
      'Draw A in the air together — up, down, across',
      'Find something nearby that starts with A',
    ],
    watchFor: 'Can your child trace the shape or repeat the sound?',
    outcomeTag: 'Builds letter–sound connection — the key that unlocks reading.',
    kicker: 'You just taught sound-letter connection — the key that unlocks reading.',
  },
  4: {
    title: 'Meet the A-Team — Short A Words',
    theme: 'CVC Decoding & Blending',
    icon: '/assets/P4F.webp',
    color: 'from-indigo-400 to-purple-500',
    accent: { border: 'border-purple-300', bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-400' },
    ages: '4-5',
    phaseName: 'Readers',
    stepName: 'Warm-Up: Short A Sounds',
    hook: 'Decode short-A words using household objects and sound practice.',
    script: [
      '"The letter A makes the sound /a/ — like apple!"',
      '"Let\'s say it together: /a/ /a/ /a/!"',
    ],
    doThis: [
      'Find 3 things that have the /a/ sound',
      'Sound out each word slowly together: c-a-t',
    ],
    watchFor: 'Can your child blend the sounds into a word?',
    outcomeTag: 'Builds blending — sounding out real words for the first time.',
    kicker: 'You just practiced sounding out real words. Your child is reading.',
  },
};

const WAVE_PATHS = {
  1: 'M0,64 C480,120 960,0 1440,64 L1440,120 L0,120 Z',
  2: 'M0,96 C240,32 480,128 720,64 C960,0 1200,96 1440,48 L1440,120 L0,120 Z',
  3: 'M0,32 C360,128 900,0 1440,80 L1440,120 L0,120 Z',
};

/* ─────────────────────────────────────────────
   /try — Ad landing page
   Stripped-down, single-CTA page for paid traffic.
   Matches Facebook ad copy: "You are the missing
   piece. Not an app."
   ───────────────────────────────────────────── */
const TryPage = () => {
  const navigate = useNavigate();
  const [founderFaceMissing, setFounderFaceMissing] = useState(false);
  const [isVisible, setIsVisible] = useState({ proof: true });
  const [activePhase, setActivePhase] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const headerRef = useRef(null);
  const visibleRef = useRef({ proof: true });

  usePageView('try');

  /* Header shrink via CSS custom-property — no React re-renders */
  useEffect(() => {
    let rafId = null;
    const handleHeaderShrink = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const heroHeight = window.innerHeight * 0.7;
        const progress = Math.min(window.scrollY / heroHeight, 1);
        if (headerRef.current) {
          headerRef.current.style.setProperty('--shrink', progress);
        }
      });
    };
    window.addEventListener('scroll', handleHeaderShrink, { passive: true });
    handleHeaderShrink();
    return () => {
      window.removeEventListener('scroll', handleHeaderShrink);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const isMd = typeof window !== 'undefined' && window.innerWidth >= 768;
  const h = {
    logoPx: isMd ? 44 : isMobile ? 30 : 38,
    logoMin: isMd ? 10 : isMobile ? 6 : 8,
    titlePx: isMd ? 28 : isMobile ? 18 : 22,
    titleMin: isMd ? 8 : isMobile ? 4 : 6,
    navPadY: 10,
    navPadX: 16,
    navMin: 4,
  };

  /* Scroll-depth analytics */
  useEffect(() => {
    const milestones = new Set();
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
        if (maxScroll > 0) {
          const scrollPct = Math.round((window.scrollY / maxScroll) * 100);
          [25, 50, 75, 100].forEach(m => {
            if (scrollPct >= m && !milestones.has(m)) {
              milestones.add(m);
              trackEvent('scroll_depth', { depth: m, page: 'try' });
            }
          });
        }
        ticking = false;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /* IntersectionObserver — fade-in + section-view analytics */
  useEffect(() => {
    const trackedSections = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        const newlyVisible = {};
        entries.forEach((entry) => {
          const id = entry.target.id;
          if (entry.isIntersecting && id && !visibleRef.current[id]) {
            visibleRef.current[id] = true;
            newlyVisible[id] = true;
            if (!trackedSections.has(id)) {
              trackedSections.add(id);
              trackEvent('section_view', { section: id });
            }
          }
        });
        if (Object.keys(newlyVisible).length > 0) {
          setIsVisible((prev) => ({ ...prev, ...newlyVisible }));
        }
      },
      { threshold: 0.1 }
    );
    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  /* Navigation helpers */
  const handleNavigate = (path) => {
    if (path === '/free-activity') {
      trackEvent('cta_click', { button: 'free_trial', location: 'try_page' });
      navigate(path, { state: { skipIntro: true, navSource: 'try_free_trial_cta' } });
      return;
    }
    navigate(path, { state: { navSource: 'try_nav' } });
  };

  /* Phase-tab fade transition */
  const handlePhaseSwitch = (phase) => {
    if (phase === activePhase || isTransitioning) return;
    trackEvent('cta_click', { button: 'phase_tab', phase, location: 'try_activity_preview' });
    setIsTransitioning(true);
    setTimeout(() => {
      setActivePhase(phase);
      setIsTransitioning(false);
    }, 300);
  };

  const preview = PHASE_PREVIEWS[activePhase];

  /* Inline wave divider */
  const WaveDivider = ({ topColor = '#ffffff', bottomColor = '#f9fafb', variant = 1 }) => (
    <div className="w-full overflow-hidden leading-[0]" style={{ background: topColor }}>
      <svg viewBox="0 0 1440 120" preserveAspectRatio="none" className="w-full h-[60px] sm:h-[80px] md:h-[100px] block">
        <path d={WAVE_PATHS[variant] || WAVE_PATHS[1]} fill={bottomColor} />
      </svg>
    </div>
  );

  /* ─────────────────── RENDER ─────────────────── */
  return (
    <div className="min-h-screen min-h-[100dvh] bg-white flex flex-col relative animate-page-enter" style={{ overflowX: 'clip' }}>

      {/* ============================================ */}
      {/* FIXED HEADER — Logo only, no competing CTAs  */}
      {/* ============================================ */}
      <header ref={headerRef} className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm" style={{ '--shrink': 0 }}>
        <div className="border-b border-purple-100" style={{ padding: `calc(${h.navPadY}px - var(--shrink) * ${h.navMin}px) calc(${h.navPadX}px - var(--shrink) * ${h.navMin}px)` }}>
          {/* min-h matches HomePage header height so the spacer div below stays consistent */}
          <div className="max-w-7xl mx-auto flex justify-between items-center gap-1 sm:gap-2 min-h-[36px] sm:min-h-[38px] md:min-h-[42px]">
            <button onClick={() => handleNavigate('/')} className="flex items-center gap-1.5 sm:gap-3 group flex-shrink-0 min-w-0">
              <img src="/assets/brlogo2.webp" alt="BrightReading Logo" className="w-auto object-contain group-hover:scale-110 transition transform flex-shrink-0" style={{ height: `calc(${h.logoPx}px - var(--shrink) * ${h.logoMin}px)` }} />
              <h1 className="font-black bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent whitespace-nowrap" style={{ fontSize: `calc(${h.titlePx}px - var(--shrink) * ${h.titleMin}px)` }}>BrightReading</h1>
            </button>
          </div>
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-[56px] sm:h-[58px] md:h-[62px]"></div>

      {/* ============================================ */}
      {/* HERO — Ad headline + Activity Preview        */}
      {/* ============================================ */}
      <section className="bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 pb-[60px] sm:pb-[80px] md:pb-[100px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-14 lg:pt-20 pb-2 sm:pb-4">

          {/* Headline + Subhead — centered */}
          <div className="text-center max-w-4xl mx-auto mb-8 sm:mb-10">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-4 sm:mb-5 leading-tight">
              Your child learns to read from{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 italic">you.</span>
              {' '}Not from a screen.
            </h2>

            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-gray-700 font-normal mb-6 sm:mb-8 leading-snug max-w-2xl mx-auto">
              Step-by-step scripts that build real reading skills &mdash; together, no screen, 10 minutes a day.
            </p>
          </div>

          {/* Interactive Activity Preview with age tabs */}
          <div className="max-w-6xl mx-auto mb-8 sm:mb-10">
            <p className="text-center text-sm text-gray-400 font-medium mb-3">
              Your 10-minute script for tonight
            </p>
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">

              {/* Phase tabs — mobile horizontal scroll with arrows */}
              <div className="lg:w-[260px] flex-shrink-0">
                <div className="flex lg:hidden items-center gap-1 pb-3">
                  <button
                    onClick={() => { const p = activePhase > 1 ? activePhase - 1 : 4; handlePhaseSwitch(p); }}
                    className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600 transition"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <div className="flex-1 flex overflow-x-auto gap-2 scrollbar-none">
                    {Object.entries(PHASE_PREVIEWS).map(([key, phase]) => {
                      const num = parseInt(key);
                      const active = activePhase === num;
                      return (
                        <button
                          key={key}
                          onClick={() => handlePhaseSwitch(num)}
                          className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-full font-bold text-sm transition-all whitespace-nowrap ${
                            active
                              ? `bg-gradient-to-r ${phase.color} text-white shadow-lg`
                              : 'bg-white border-2 border-gray-200 text-gray-600 hover:border-purple-300'
                          }`}
                        >
                          <img src={phase.icon} alt="" className={`w-6 h-6 object-contain ${active ? 'drop-shadow-md' : ''}`} />
                          Ages {phase.ages}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => { const p = activePhase < 4 ? activePhase + 1 : 1; handlePhaseSwitch(p); }}
                    className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600 transition"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>

                {/* Phase tabs — desktop vertical */}
                <div className="hidden lg:flex lg:flex-col gap-2">
                  {Object.entries(PHASE_PREVIEWS).map(([key, phase]) => {
                    const num = parseInt(key);
                    const active = activePhase === num;
                    return (
                      <button
                        key={key}
                        onClick={() => handlePhaseSwitch(num)}
                        className={`flex items-center gap-3 p-4 rounded-xl w-full text-left transition-all duration-300 ${
                          active
                            ? `bg-gradient-to-r ${phase.color} text-white shadow-lg scale-[1.02]`
                            : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-purple-300 hover:shadow-md'
                        }`}
                      >
                        <img src={phase.icon} alt="" loading="lazy" className={`w-10 h-10 object-contain flex-shrink-0 ${active ? 'drop-shadow-md' : ''}`} />
                        <div>
                          <div className="font-black text-sm">Phase {num}</div>
                          <div className={`text-xs ${active ? 'text-white/80' : 'text-gray-400'}`}>Ages {phase.ages} &middot; {phase.phaseName}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Preview card */}
              <div className="flex-1 min-w-0">
                <div className={`transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
                  <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-200 overflow-hidden">
                    {/* Step header */}
                    <div className={`bg-gradient-to-br ${preview.accent.bg} px-5 py-4 sm:py-5`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-bold ${preview.accent.text} tracking-wider uppercase`}>Step 1 of 4</span>
                        <span className={`text-xs font-semibold ${preview.accent.text} ${preview.accent.bg} rounded-full px-3 py-1`}>2 min</span>
                      </div>
                      <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">{preview.stepName}</h4>
                      <p className="text-gray-600 text-sm leading-relaxed">{preview.hook}</p>
                    </div>

                    {/* Activity plan — Say / Do */}
                    <div className={`border-t ${preview.accent.border}`}>
                      <div className={`${preview.accent.bg} px-5 py-2.5`}>
                        <h5 className={`text-sm font-bold ${preview.accent.text} tracking-wide`}>Activity Plan</h5>
                      </div>
                      <div className="p-5 space-y-5">
                        <div>
                          <h6 className={`text-xs font-bold ${preview.accent.text} tracking-wider uppercase mb-2`}>Say!</h6>
                          <div className="space-y-2">
                            {preview.script.map((line, i) => (
                              <div key={i} className={`border-l-[3px] ${preview.accent.border} ${preview.accent.bg} rounded-r-xl px-4 py-2.5`}>
                                <p className="text-gray-800 text-sm leading-relaxed">{line}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h6 className={`text-xs font-bold ${preview.accent.text} tracking-wider uppercase mb-2`}>Do!</h6>
                          <ol className="space-y-2.5">
                            {preview.doThis.map((action, i) => (
                              <li key={i} className="flex gap-3 items-start">
                                <span className={`flex-shrink-0 w-6 h-6 rounded-full ${preview.accent.dot} text-white text-xs font-bold flex items-center justify-center mt-0.5`}>{i + 1}</span>
                                <p className="text-gray-700 text-sm leading-relaxed">{action}</p>
                              </li>
                            ))}
                          </ol>
                        </div>
                      </div>
                    </div>

                    {/* Watch for */}
                    <div className="mx-5 mb-3 bg-amber-50 border-l-4 border-amber-400 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <Eye className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <h6 className="text-sm font-bold text-amber-700 mb-0.5">Watch For</h6>
                          <p className="text-gray-700 text-sm leading-relaxed">{preview.watchFor}</p>
                        </div>
                      </div>
                    </div>

                    {/* Outcome tag */}
                    <div className="mx-5 mb-4">
                      <span className="inline-block text-xs text-gray-500 font-medium bg-gray-100 rounded-full px-3 py-1">
                        {preview.outcomeTag}
                      </span>
                    </div>

                    {/* Phase badge bar */}
                    <div className={`bg-gradient-to-r ${preview.color} px-5 py-3 flex items-center gap-3`}>
                      <img src={preview.icon} alt="" className="w-6 h-6 object-contain drop-shadow-md" loading="lazy" />
                      <span className="text-white font-bold text-xs">Phase {activePhase} &middot; Week 1 &middot; Activity 1</span>
                      <span className="ml-auto bg-white/20 px-3 py-1 rounded-full text-white text-xs font-bold flex-shrink-0">10 min</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Single CTA + Micro-copy */}
          <div className="text-center">
            <div className="flex items-center gap-3 sm:gap-4 mb-4 justify-center">
              <button
                onClick={() => handleNavigate('/free-activity')}
                className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 sm:px-12 py-4 sm:py-5 rounded-full text-base sm:text-lg font-black hover:shadow-2xl transition-all transform hover:scale-105 shadow-lg inline-flex items-center justify-center gap-2"
              >
                <Play size={20} className="fill-white" />
                Try Teaching Your Toddler &mdash; Free
              </button>
            </div>
            <p className="text-sm text-gray-400 font-medium">
              No signup. No credit card. Takes under 2 minutes.
            </p>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* JEFF'S TESTIMONIAL                           */}
      {/* ============================================ */}
      <section className="relative bg-white pt-0 pb-8 sm:pb-12">
        {/* White wave that cuts up into the hero's extended gradient */}
        <div className="absolute left-0 right-0 -top-[60px] sm:-top-[80px] md:-top-[100px] w-full overflow-hidden leading-[0] pointer-events-none">
          <svg viewBox="0 0 1440 120" preserveAspectRatio="none" className="w-full h-[60px] sm:h-[80px] md:h-[100px] block">
            <path d={WAVE_PATHS[1]} fill="#ffffff" />
          </svg>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border-2 border-purple-200 shadow-lg p-6 sm:p-8">
            <div className="flex justify-center gap-0.5 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={20} className="text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <blockquote className="text-center">
              <p className="text-gray-800 text-base sm:text-lg leading-relaxed italic mb-4">
                &ldquo;Love the app! My son wants to do multiple activities per night with me and my wife! He really likes tracing the letters and saying them with me.&rdquo;
              </p>
              <footer className="text-sm font-black text-purple-700">
                &mdash; Jeff, Dad of 3 year old
              </footer>
            </blockquote>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* OUTCOME STRIP                                */}
      {/* ============================================ */}
      <section className="bg-white pt-2 pb-6 sm:pb-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <ul className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-2 sm:gap-6 text-center">
            <li className="text-sm text-gray-400 font-medium whitespace-nowrap">
              <span aria-hidden="true">&#x1F5E3;&#xFE0F;</span> Sound recognition by week 2
            </li>
            <li className="text-sm text-gray-400 font-medium whitespace-nowrap">
              <span aria-hidden="true">&#x1F4D6;</span> Letter awareness by month 2
            </li>
            <li className="text-sm text-gray-400 font-medium whitespace-nowrap">
              <span aria-hidden="true">&#x270D;&#xFE0F;</span> Early word building by month 6
            </li>
          </ul>
        </div>
      </section>

      {/* ============================================ */}
      {/* HOW IT WORKS                                 */}
      {/* ============================================ */}
      <section className="bg-white py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div
            id="how-it-works"
            data-animate
            className={`transition-all duration-1000 ${isVisible['how-it-works'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <div className="text-center mb-8 sm:mb-10">
              <h3 className="text-2xl sm:text-3xl font-black text-gray-900 mb-2">Three Steps. 5 Days a Week.</h3>
              <p className="text-gray-500 text-sm sm:text-base">No planning, no materials, no teaching degree.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              {[
                { step: '1', title: 'Open the Activity', desc: 'Today\'s activity is ready and waiting, in the right order for your child\'s age.', icon: '\u{1F4D6}', highlight: false },
                { step: '2', title: 'Read the Script', desc: 'We tell you exactly what to say and do. Word for word.', icon: '\u{1F4AC}', highlight: false },
                { step: '3', title: 'Put the Phone Down', desc: 'Do the activity face-to-face with your child. 10 minutes.', icon: '\u{1F91D}', highlight: true },
              ].map((item) => (
                <div
                  key={item.step}
                  className={`text-center rounded-2xl p-6 sm:p-8 transition-all ${
                    item.highlight
                      ? 'bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 shadow-lg'
                      : 'bg-gray-50 border border-gray-100'
                  }`}
                >
                  <div className="text-4xl sm:text-5xl mb-4">{item.icon}</div>
                  <div className="text-base sm:text-lg font-black text-gray-900 mb-2">{item.title}</div>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* RESEARCH STATS                               */}
      {/* ============================================ */}
      <WaveDivider topColor="#ffffff" bottomColor="#faf5ff" variant={3} />
      <section className="bg-gradient-to-b from-purple-50 to-white py-12 sm:py-16">
        <div
          id="proof"
          data-animate
          className={`max-w-6xl mx-auto px-4 sm:px-6 transition-all duration-700 ${isVisible['proof'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <div className="text-center mb-8">
            <h3 className="text-2xl sm:text-3xl font-black text-gray-900 mb-2">Built on Research. Not Guesswork.</h3>
            <p className="text-gray-500 text-sm sm:text-base">Features studies from MIT, Harvard, the National Literacy Panel, and more.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
            <div className="bg-white rounded-xl shadow-md border border-purple-100 p-4 md:p-6 text-left">
              <div className="text-2xl md:text-3xl font-black text-purple-600 mb-1">1,040 <span className="text-gray-900 text-base md:text-lg">Scripted activities</span></div>
              <p className="text-gray-500 text-xs md:text-sm font-medium">Four years of your child's reading foundation. Done for you.</p>
            </div>
            <div className="bg-white rounded-xl shadow-md border border-purple-100 p-4 md:p-6 text-left">
              <div className="text-2xl md:text-3xl font-black text-pink-600 mb-1">50+ <span className="text-gray-900 text-base md:text-lg">Peer-reviewed studies</span></div>
              <p className="text-gray-500 text-xs md:text-sm font-medium">Every activity cites its research. You'll know exactly why it works.</p>
            </div>
            <div className="bg-white rounded-xl shadow-md border border-purple-100 p-4 md:p-6 text-left">
              <div className="text-2xl md:text-3xl font-black text-orange-500 mb-1">4 <span className="text-gray-900 text-base md:text-lg">Developmental phases</span></div>
              <p className="text-gray-500 text-xs md:text-sm font-medium">Age 1 through 5. The curriculum grows as your child does.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FOUNDER PILL — links to /research            */}
      {/* ============================================ */}
      {!founderFaceMissing && (
        <section className="bg-white py-6 sm:py-8">
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => { trackEvent('cta_click', { button: 'hero_pill_founder', location: 'try_page' }); handleNavigate('/research'); }}
              className="inline-flex items-center gap-3 bg-white border border-purple-100 hover:border-purple-300 rounded-full pl-1 pr-5 py-1.5 shadow-sm hover:shadow-md transition group"
              title="Read the research"
            >
              <img
                src="/assets/founder-face.webp"
                alt="Luc, founder of BrightReading"
                onError={() => setFounderFaceMissing(true)}
                className="w-10 h-10 sm:w-11 sm:h-11 rounded-full object-cover border-2 border-purple-200 group-hover:border-purple-400 transition flex-shrink-0"
              />
              <span className="text-left">
                <span className="block text-xs sm:text-sm font-black text-gray-900 leading-tight">A 22-year-old with no kids built this.</span>
                <span className="block text-[11px] sm:text-xs text-purple-500 group-hover:text-purple-700 font-semibold leading-tight transition">Find out why &rarr;</span>
              </span>
            </button>
          </div>
        </section>
      )}

      {/* ============================================ */}
      {/* FINAL CTA — no pricing, no account link      */}
      {/* ============================================ */}
      <section className="bg-white py-12 sm:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div
            id="final-cta"
            data-animate
            className={`bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white rounded-2xl shadow-2xl p-6 sm:p-8 md:p-12 text-center relative overflow-hidden transition-all duration-1000 ${isVisible['final-cta'] ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}
          >
            <div className="relative z-10 max-w-2xl mx-auto">
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-black mb-3 leading-tight">
                They won't be this age tomorrow.
              </h3>
              <p className="text-sm sm:text-lg md:text-xl mb-6 sm:mb-8 opacity-90">
                One activity. See it in 2 minutes. No signup required.
              </p>
              <div className="flex flex-col items-center gap-3">
                <button
                  onClick={() => handleNavigate('/free-activity')}
                  className="w-full sm:w-auto bg-white text-purple-600 px-8 sm:px-10 py-4 sm:py-5 rounded-full text-base sm:text-lg md:text-xl font-black hover:bg-gray-50 transition shadow-xl transform hover:scale-105 inline-flex items-center justify-center gap-2 sm:gap-3"
                >
                  <Play size={22} className="fill-purple-600" />
                  Try Teaching Your Toddler &mdash; Free
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FOOTER                                       */}
      {/* ============================================ */}
      <footer className="bg-gradient-to-r from-purple-900 via-pink-900 to-orange-900 text-white relative z-10">
        <div className="w-full overflow-hidden leading-[0]" style={{ marginBottom: '-1px' }}>
          <svg viewBox="0 0 1440 120" preserveAspectRatio="none" className="w-full h-[60px] sm:h-[80px] md:h-[100px] block">
            <path d="M0,0 L1440,0 L1440,32 C900,128 360,0 0,80 Z" fill="#ffffff" />
          </svg>
        </div>
        <div className="max-w-6xl mx-auto px-4 py-8 sm:py-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div className="col-span-2 lg:col-span-1">
              <div className="flex items-center gap-2 mb-3">
                <img src="/assets/brlogo.webp" alt="BrightReading Logo" className="h-8 w-auto object-contain flex-shrink-0" />
                <h3 className="text-xl font-black">BrightReading</h3>
              </div>
              <p className="text-purple-200 leading-relaxed text-sm">1,040 research-backed activities for ages 1-5. You lead. No screen.</p>
            </div>
            <div>
              <h4 className="font-black text-base mb-3">Product</h4>
              <ul className="space-y-2 text-purple-200 text-sm">
                <li><button onClick={() => handleNavigate('/free-activity')} className="hover:text-white transition hover:underline">Try a Free Activity</button></li>
                <li><button onClick={() => handleNavigate('/dashboard')} className="hover:text-white transition hover:underline">Dashboard</button></li>
                <li><button onClick={() => handleNavigate('/login')} className="hover:text-white transition hover:underline">Sign Up</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-black text-base mb-3">Resources</h4>
              <ul className="space-y-2 text-purple-200 text-sm">
                <li><button onClick={() => handleNavigate('/parent-guide')} className="hover:text-white transition hover:underline">Parent Guide</button></li>
                <li><button onClick={() => handleNavigate('/research')} className="hover:text-white transition hover:underline">Research</button></li>
                <li><button onClick={() => handleNavigate('/faq')} className="hover:text-white transition hover:underline">FAQs</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-black text-base mb-3">Company</h4>
              <ul className="space-y-2 text-purple-200 text-sm">
                <li><button onClick={() => handleNavigate('/about')} className="hover:text-white transition hover:underline">About</button></li>
                <li><button onClick={() => handleNavigate('/contact')} className="hover:text-white transition hover:underline">Contact</button></li>
                <li><button onClick={() => handleNavigate('/privacy')} className="hover:text-white transition hover:underline">Privacy Policy</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 pt-5 flex flex-col md:flex-row justify-between items-center gap-3 text-sm text-purple-200">
            <p>&copy; 2026 BrightReading. All rights reserved.</p>
            <div className="flex gap-6">
              <button onClick={() => handleNavigate('/privacy')} className="hover:text-white transition hover:underline">Privacy</button>
              <button onClick={() => handleNavigate('/terms')} className="hover:text-white transition hover:underline">Terms</button>
              <button onClick={() => handleNavigate('/contact')} className="hover:text-white transition hover:underline">Contact</button>
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out;
        }
        .scrollbar-none::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-none {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default TryPage;
